import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, Search, Heart, User, HousePlus } from 'lucide-react';
import { storage } from '@/lib/storage';
import { cn } from '@/lib/utils';

export const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const language = i18n.language === 'pt-BR' || i18n.language === 'pt' ? 'pt' : 'en';
  const [user, setUser] = useState(null);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const texts = {
    pt: {
      home: 'Início',
      search: 'Buscar',
      favorites: 'Favoritos',
      listProperty: 'Anunciar',
      account: 'Conta',
      login: 'Entrar'
    },
    en: {
      home: 'Home',
      search: 'Search',
      favorites: 'Favorites',
      listProperty: 'List',
      account: 'Account',
      login: 'Login'
    }
  };

  const t = texts[language];

  useEffect(() => {
    setUser(storage.getUser());
    
    const updateCounts = () => {
      const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
      setFavoritesCount(favorites.length);
    };

    updateCounts();
    window.addEventListener('storage', updateCounts);
    window.addEventListener('favoritesChanged', updateCounts);

    return () => {
      window.removeEventListener('storage', updateCounts);
      window.removeEventListener('favoritesChanged', updateCounts);
    };
  }, []);

  // Hide on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const navItems = [
    {
      icon: Home,
      label: t.home,
      path: '/',
      badge: null
    },
    {
      icon: Search,
      label: t.search,
      path: '/search',
      badge: null
    },
    {
      icon: Heart,
      label: t.favorites,
      path: '/favorites',
      badge: favoritesCount > 0 ? favoritesCount : null,
      requiresAuth: true
    },
    {
      icon: HousePlus,
      label: t.listProperty,
      path: '/host/create-property',
      badge: null
    },
    {
      icon: User,
      label: user ? t.account : t.login,
      path: user ? '/minha-conta' : '/login',
      badge: null
    }
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Don't show on certain pages
  const hiddenPaths = ['/login', '/register', '/forgot-password', '/reset-password'];
  if (hiddenPaths.some(p => location.pathname.startsWith(p))) {
    return null;
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.nav
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          exit={{ y: 100 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-white/95 backdrop-blur-lg border-t border-gray-200 shadow-lg safe-area-bottom"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className="flex items-center justify-around h-16 px-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              if (item.requiresAuth && !user) return null;
              
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={cn(
                    'relative flex flex-col items-center justify-center w-full h-full gap-1 transition-all duration-200',
                    active ? 'text-[#00C2D1]' : 'text-gray-500 hover:text-gray-700'
                  )}
                  data-testid={`mobile-nav-${item.path.replace('/', '') || 'home'}`}
                >
                  <div className="relative">
                    <Icon className={cn('w-5 h-5', active && 'scale-110')} />
                    {item.badge && (
                      <span className="absolute -top-1 -right-2 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full px-1">
                        {item.badge > 9 ? '9+' : item.badge}
                      </span>
                    )}
                  </div>
                  <span className={cn(
                    'text-[10px] font-medium transition-all',
                    active ? 'opacity-100' : 'opacity-70'
                  )}>
                    {item.label}
                  </span>
                  {active && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-[#00C2D1] rounded-full"
                      transition={{ type: 'spring', damping: 30, stiffness: 400 }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </motion.nav>
      )}
    </AnimatePresence>
  );
};

export default MobileBottomNav;
